import classNames from 'classnames';
import * as inflection from 'inflection';
import * as _ from 'lodash-es';
import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  ProvisionSelectOption,
  ProvisionSelectSingleValue,
  provisionsToOptions,
} from '../admin/provisions/ProvisionSelectOption';
import {
  ProvisionTypeSelectOption,
  ProvisionTypeSelectSingleValue,
} from '../admin/provisions/ProvisionTypeSelectOption';
import useProvisionEntities from '../admin/provisions/useProvisionEntities';
import useProvisionEntitySearchConfig from '../admin/provisions/useProvisionEntitySearchConfig';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import { Field, FieldSet } from '../components/Form';
import FormModal from '../components/FormModal';
import LoadingIndicatorModal from '../components/LoadingIndicatorModal';
import SVGIcon from '../components/SVGIcon';
import PostMention from '../editor/mentions/PostMention';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import {
  InvitationSource,
  PlanTier,
  ProvisionType,
  UserType,
} from '../graphql/generated/graphql';
import caretIcon from '../icons/non-prefilled/16x16/caret.svg';
import PostDetailPreviewPopOver from '../post/PostDetailPreviewPopOver';
import type { EntitySelectConfig } from '../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../search/entitySearch/CategorizedEntitySelect';
import { invitationToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import handleApolloError from '../utils/error/handleApolloError';
import { nonEmptyId } from '../utils/nonEmpty';
import * as styles from './InvitationModal.module.scss';
import InvitationSeatCallout from './InvitationSeatCallout';
import type { InvitationUserSelectOptionType } from './InvitationUserSelect';
import InvitationUserSelect from './InvitationUserSelect';
import SSOBanner from './SSOBanner';
import SignUpLinkTip from './SignUpLinkTip';
import useInviteMutation from './useInviteMutation';
import useSeatLimits from './useSeatLimits';

type InvitationProvisionFormValues = {
  name: string;
  memberIds: string[];
  favoriteIds: string[];
  type: ProvisionType;
  urlEnabled: boolean;
};

interface InvitationFormValues {
  invites: InvitationUserSelectOptionType[];
  provisionId: string | null;
  updatedProvision: InvitationProvisionFormValues;
}

const INITIAL_POST_FRAGMENT = gql(`
  fragment InvitationModal_initialPost on Post {
    id
    title
  }
`);

const INVITATION_MODAL_PROVISION_FRAGMENT = gql(`
  fragment InvitationModal_provision on Provision {
    id
    name
    type
    urlEnabled
    isSsoDefault
    favorites {
      ... on Post {
        id
      }
      ... on Topic {
        id
      }
    }
    members {
      ... on Topic {
        id
      }
      ... on Post {
        id
      }
      ... on Group {
        id
      }
    }
    ...ProvisionSelectOption_provision
    ...ProvisionSelectSingleValue_provision
    ...SignUpLinkTip_provision
  }
`);

type ProvisionFragment = DocumentType<
  typeof INVITATION_MODAL_PROVISION_FRAGMENT
>;

const INVITATION_MODAL_QUERY = gql(/** @complexity 13 */ `
  query InvitationModal {
    session: currentSession {
      organization {
        id
        planTier
        provisions {
          ...InvitationModal_provision
        }
        settings {
          authentication
          requireInviteApproval
        }
      }
    }
  }
`);

const getProvisions = (
  provisions: ProvisionFragment[] | null,
  isSeatLimitMaxed: boolean,
  isGuestLimitMaxed: boolean,
  isAdmin: boolean,
) => {
  const filteredProvisions =
    provisions?.filter(nonEmptyId).filter((provision) => {
      const type = provision?.type;
      if (!type) return false;
      if (type === ProvisionType.Guest) return !isGuestLimitMaxed;
      if (type === ProvisionType.Admin) return !isSeatLimitMaxed && isAdmin;
      return !isSeatLimitMaxed;
    }) ?? [];

  return _.sortBy(filteredProvisions, (provision) => [
    [true, false].indexOf(provision.isSsoDefault === true),
    [ProvisionType.User, ProvisionType.Admin, ProvisionType.Guest].indexOf(
      provision.type,
    ),
    provision.name,
  ]);
};

const getCurrentProvisionFormValues = (
  provisionId: string | null,
  provisions: ProvisionFragment[],
  isSeatLimitMaxed: boolean,
  previousValues?: Partial<InvitationProvisionFormValues>,
): InvitationProvisionFormValues => {
  const provision = provisions?.find((p) => p.id === provisionId) ?? null;

  const defaultProvisionType = isSeatLimitMaxed
    ? ProvisionType.Guest
    : ProvisionType.User;

  return {
    favoriteIds: provision?.favorites.map((p) => p.id) ?? [],
    memberIds:
      provision?.members.map((p) => p.id) ?? previousValues?.memberIds ?? [],
    name: provision?.name || '',
    type: provision?.type ?? previousValues?.type ?? defaultProvisionType,
    urlEnabled: provision?.urlEnabled ?? false,
  };
};

const InvitationModalProvisionFormFields = ({
  setFieldValue,
  values,
  needApproval,
  originalProvision,
  initialPostId,
  seatLimitMaxed,
}: {
  setFieldValue: (
    field: string,
    value:
      | InvitationFormValues['updatedProvision'][keyof InvitationFormValues['updatedProvision']]
      | InvitationFormValues['provisionId'],
  ) => void;
  values: InvitationFormValues;
  needApproval?: boolean | null;
  originalProvision: ProvisionFragment | null;
  initialPostId?: string;
  seatLimitMaxed: boolean;
}) => {
  const favoriteSearchConfig = useProvisionEntitySearchConfig(
    values.updatedProvision,
  );

  const { favorites, memberships } = useProvisionEntities(
    values.updatedProvision,
  );

  const config = useMemo<EntitySelectConfig>(
    () => ({
      topic: { preferredMaxCount: 3 },
      group: { preferredMaxCount: 3 },
      post: {
        filter: initialPostId ? ({ id }) => id !== initialPostId : undefined,
        showIfNoOthersMatch: true,
      },
      order: ['topic', 'group', 'post'],
    }),
    [initialPostId],
  );

  if (!favoriteSearchConfig) {
    return null;
  }

  return (
    <>
      {values.updatedProvision.urlEnabled &&
        !needApproval &&
        originalProvision && (
          <div className={styles.signUpLinkTipContainer}>
            <SignUpLinkTip provision={originalProvision} />
          </div>
        )}
      <Field
        label="Role"
        type="select"
        name="updatedProvision.type"
        isClearable={false}
        options={_.compact([
          !seatLimitMaxed && {
            description: 'Standard team seat',
            value: ProvisionType.User,
          },
          !seatLimitMaxed && {
            description: 'Unlocks all privileges',
            value: ProvisionType.Admin,
          },
          {
            description: 'Limited access to topics',
            value: ProvisionType.Guest,
          },
        ])}
        onChange={() => {
          setFieldValue('provisionId', null);
        }}
        components={{
          Option: ProvisionTypeSelectOption,
          SingleValue: ProvisionTypeSelectSingleValue,
        }}
      />
      <FieldSet legend="Membership">
        <CategorizedEntitySelect
          maxOptionsToShow={6}
          isClearable={false}
          config={config}
          onChange={(entities) => {
            setFieldValue('provisionId', null);
            setFieldValue(
              'updatedProvision.memberIds',
              entities.map((e) => e.id),
            );
          }}
          value={memberships}
          isMulti
        />
      </FieldSet>
      <div className={styles.section}>
        <FieldSet legend="Favorites">
          <CategorizedEntitySelect
            isClearable={false}
            onChange={(entities) => {
              setFieldValue('provisionId', null);
              setFieldValue(
                'updatedProvision.favoriteIds',
                entities.map((e) => e.id),
              );
            }}
            value={favorites}
            isMulti
            {...favoriteSearchConfig}
          />
        </FieldSet>
      </div>
    </>
  );
};

interface InvitationModalProps {
  close: () => void;
  expanded?: boolean;
  initialOptions?: InvitationUserSelectOptionType[];
  initialValues?: {
    post?: DocumentType<typeof INITIAL_POST_FRAGMENT>;
    memberIds?: string[];
  };
}

const InvitationModal = ({
  close,
  expanded = false,
  initialOptions = [],
  initialValues,
}: InvitationModalProps) => {
  const dispatch = useAppDispatch();
  const [isExpanded, setIsExpanded] = useState(expanded);
  const invite = useInviteMutation();

  const currentUser = useAppSelector((state) => state.session.user);

  const isAdmin = currentUser.type === UserType.Admin;

  const { data, loading } = useQuery(INVITATION_MODAL_QUERY, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const { session } = data ?? {};
  const {
    isSeatLimitMaxed,
    isGuestLimitMaxed,
    isLoading: isSeatLimitsLoading,
  } = useSeatLimits();

  const isLoading = loading || isSeatLimitsLoading;

  const provisions = getProvisions(
    session?.organization?.provisions ?? [],
    isSeatLimitMaxed,
    isGuestLimitMaxed,
    isAdmin,
  );

  const isRequiringInviteApprovalSetting =
    session?.organization?.settings?.requireInviteApproval;
  const planTier = session?.organization?.planTier;

  const sendInvite = async (
    email: string,
    name: string | null,
    updatedProvision: InvitationProvisionFormValues,
  ) => {
    try {
      return await invite({
        variables: {
          email,
          name,
          favoriteIds: updatedProvision.favoriteIds,
          memberIds: updatedProvision.memberIds,
          type: updatedProvision.type,
          source: initialValues?.post ? InvitationSource.Post : undefined,
        },
      });
    } catch (error) {
      const label = name ?? email;

      handleApolloError(error, {
        USER_EXISTS: () =>
          dispatch(
            UIActions.pushToast(invitationToasts.userExistsWarning({ label })),
          ),
        USER_INVITED: () =>
          dispatch(
            UIActions.pushToast(invitationToasts.userInvitedWarning({ label })),
          ),
        RATE_LIMIT: () =>
          dispatch(
            UIActions.pushToast(invitationToasts.rateLimitError({ label })),
          ),
      });
      return false;
    }
  };

  const isApprovalNeeded = !isAdmin && isRequiringInviteApprovalSetting;

  const onSubmit = async ({
    invites,
    updatedProvision,
  }: InvitationFormValues) => {
    const results = await Promise.all(
      invites.map((item) =>
        sendInvite(
          item.value,
          item.label !== item.value ? item.label : null,
          updatedProvision,
        ),
      ),
    );
    const sentInvites = invites.filter((_result, index) => results[index]);
    const totalSent = sentInvites.length;
    if (totalSent > 0) {
      dispatch(
        UIActions.pushToast(
          isApprovalNeeded
            ? invitationToasts.invitationRequestSent({ totalSent })
            : invitationToasts.invitationSent({ totalSent }),
        ),
      );
    }
    close();
  };

  const isFormDisabled = isSeatLimitMaxed && planTier === PlanTier.Free;

  if (isLoading) return <LoadingIndicatorModal />;

  return (
    <FormModal<InvitationFormValues>
      variant="expanded"
      close={close}
      title={isApprovalNeeded ? 'Request an invitation' : 'Invite teammates'}
      form={{
        initialValues: {
          invites: initialOptions,
          updatedProvision: getCurrentProvisionFormValues(
            null,
            provisions,
            isSeatLimitMaxed,
            {
              memberIds: [
                ...(initialValues?.memberIds ?? []),
                ...(initialValues?.post?.id ? [initialValues.post.id] : []),
              ],
            },
          ),
          provisionId: null,
        },
        onSubmit,
      }}
      footer={({ values }) => {
        const inviteCount = values.invites.length;
        const isDisabled = isFormDisabled || inviteCount === 0;

        return {
          submitButton: {
            text: `${isApprovalNeeded ? 'Request' : 'Invite'} ${inviteCount} ${inflection.inflect('teammate', inviteCount)}`,
            disabled: isDisabled,
          },
        };
      }}
    >
      {({ values, setFieldValue, setValues }) => {
        const originalProvision =
          provisions?.find((p) => p.id === values.provisionId) ?? null;

        const inviteCount = values.invites.length;

        return (
          <>
            {isSeatLimitMaxed && (
              <div className={styles.bannerContainer}>
                <InvitationSeatCallout guestLimitReached={isGuestLimitMaxed} />
              </div>
            )}
            <div className={styles.bannerContainer}>
              <SSOBanner
                provisionType={values.updatedProvision?.type}
                authenticationSetting={
                  session?.organization?.settings?.authentication
                }
              />
            </div>
            <FieldSet>
              <InvitationUserSelect
                value={values.invites}
                onChange={(v) => setFieldValue('invites', v)}
                disabled={isSeatLimitMaxed && isGuestLimitMaxed}
                placeholder="Enter a name or email of a teammate"
              />
            </FieldSet>

            {initialValues?.post &&
              values.updatedProvision.memberIds.includes(
                initialValues.post.id,
              ) && (
                <div className={styles.initialPost}>
                  {inflection.inflect('Invitee', inviteCount)} will be{' '}
                  {inflection.inflect('Editor', inviteCount)} for&nbsp;
                  <PostDetailPreviewPopOver
                    allowCrossAxisFlip
                    display="inline"
                    postId={initialValues.post.id}
                  >
                    <PostMention
                      link={false}
                      id={initialValues.post.id}
                      content={initialValues.post.title}
                      hideIcon
                      size="small"
                    />
                  </PostDetailPreviewPopOver>
                </div>
              )}

            {!isExpanded && (
              <>
                <div className={styles.note}>
                  Teammates will be invited as{' '}
                  <strong>
                    {values.updatedProvision.type === ProvisionType.Guest &&
                      'Guest'}
                    {values.updatedProvision.type === ProvisionType.User &&
                      'Standard User'}
                  </strong>{' '}
                  unless you customize the invitation
                </div>
                <Button
                  variant="secondary"
                  block
                  onClick={() => setIsExpanded(true)}
                  disabled={isSeatLimitMaxed && isGuestLimitMaxed}
                >
                  Show additional settings
                  <SVGIcon className={styles.buttonCaret} size={8}>
                    {caretIcon}
                  </SVGIcon>
                </Button>
              </>
            )}
            <div
              className={classNames(styles.expandableSection, {
                [styles.expanded]: isExpanded,
              })}
            >
              {provisions.length > 0 && (
                <Field
                  name="provisionId"
                  options={provisionsToOptions(provisions)}
                  type="select"
                  label="Provision"
                  disabled={isFormDisabled}
                  isClearable
                  placeholder="No provision assigned"
                  onChange={(newId) =>
                    setValues({
                      ...values,
                      provisionId: newId,
                      updatedProvision: getCurrentProvisionFormValues(
                        newId,
                        provisions,
                        isSeatLimitMaxed,
                        { type: values.updatedProvision.type },
                      ),
                    })
                  }
                  components={{
                    Option: ProvisionSelectOption,
                    SingleValue: ProvisionSelectSingleValue,
                  }}
                />
              )}
              <InvitationModalProvisionFormFields
                setFieldValue={setFieldValue}
                values={values}
                needApproval={isApprovalNeeded}
                originalProvision={originalProvision}
                initialPostId={initialValues?.post?.id}
                seatLimitMaxed={isSeatLimitMaxed}
              />
            </div>
            <div className={styles.separator} />
          </>
        );
      }}
    </FormModal>
  );
};

export default InvitationModal;
